/**
 * Timetable Service - API client for periods, class/teacher timetables,
 * PDF export, and the teacher-facing schedule sub-router
 */
import api from '../utils/api';

const timetableService = {
  // ---- Periods ----

  listPeriods: async () => {
    try {
      return await api.get('/api/timetable/periods');
    } catch (error) {
      console.error('Error fetching periods:', error);
      throw error;
    }
  },

  seedDefaultPeriods: async () => {
    try {
      return await api.post('/api/timetable/periods/seed-defaults', {});
    } catch (error) {
      console.error('Error seeding default periods:', error);
      throw error;
    }
  },

  createPeriod: async (data) => {
    try {
      return await api.post('/api/timetable/periods', data);
    } catch (error) {
      console.error('Error creating period:', error);
      throw error;
    }
  },

  updatePeriod: async (periodId, data) => {
    try {
      return await api.patch(`/api/timetable/periods/${periodId}`, data);
    } catch (error) {
      console.error('Error updating period:', error);
      throw error;
    }
  },

  deletePeriod: async (periodId) => {
    try {
      return await api.delete(`/api/timetable/periods/${periodId}`);
    } catch (error) {
      console.error('Error deleting period:', error);
      throw error;
    }
  },

  // ---- Class / teacher timetables ----

  getClassTimetable: async (classId, academicTermId) => {
    try {
      return await api.get(`/api/timetable/class/${classId}`, { params: { academic_term_id: academicTermId } });
    } catch (error) {
      console.error('Error fetching class timetable:', error);
      throw error;
    }
  },

  getTeacherTimetable: async (teacherId, academicTermId) => {
    try {
      return await api.get(`/api/timetable/teacher/${teacherId}`, { params: { academic_term_id: academicTermId } });
    } catch (error) {
      console.error('Error fetching teacher timetable:', error);
      throw error;
    }
  },

  createEntry: async (data) => {
    try {
      return await api.post('/api/timetable', data);
    } catch (error) {
      console.error('Error creating timetable entry:', error);
      throw error;
    }
  },

  deleteEntry: async (entryId) => {
    try {
      return await api.delete(`/api/timetable/${entryId}`);
    } catch (error) {
      console.error('Error deleting timetable entry:', error);
      throw error;
    }
  },

  exportClassTimetablePdf: async (classId, academicTermId) => {
    try {
      return await api.get(`/api/timetable/class/${classId}/export-pdf`, {
        params: { academic_term_id: academicTermId }, raw: true
      });
    } catch (error) {
      console.error('Error exporting class timetable PDF:', error);
      throw error;
    }
  },

  exportTeacherTimetablePdf: async (teacherId, academicTermId) => {
    try {
      return await api.get(`/api/timetable/teacher/${teacherId}/export-pdf`, {
        params: { academic_term_id: academicTermId }, raw: true
      });
    } catch (error) {
      console.error('Error exporting teacher timetable PDF:', error);
      throw error;
    }
  },

  // ---- Teacher's own schedule (teacher/timetable.py) ----

  getMySchedule: async () => {
    try {
      return await api.get('/api/teacher/timetable/my-schedule');
    } catch (error) {
      console.error('Error fetching my schedule:', error);
      throw error;
    }
  },

  getTeacherClassSchedule: async (classId) => {
    try {
      return await api.get(`/api/teacher/timetable/class/${classId}`);
    } catch (error) {
      console.error('Error fetching class schedule:', error);
      throw error;
    }
  },

  getTeacherDaySchedule: async (day) => {
    try {
      return await api.get(`/api/teacher/timetable/day/${day}`);
    } catch (error) {
      console.error('Error fetching day schedule:', error);
      throw error;
    }
  },

  listTeacherPeriods: async () => {
    try {
      return await api.get('/api/teacher/timetable/periods');
    } catch (error) {
      console.error('Error fetching teacher periods:', error);
      throw error;
    }
  }
};

export default timetableService;
